import { ElementRef, Renderer2, inject } from "@angular/core";
import { Router } from "@angular/router";
import { ConfigureOverlayComponent } from "src/app/components/configure-overlay/configure-overlay.component";
import { CLICK_ACTION_FOR_FLUTTER, NAVIGATION_PATH_BY_CHOICE, SEARCH_QUERY_PARAM_KEYS, path_based_links, search_url_links } from "src/app/core/constants/dynamic-click-actions.constant";
import { BrowserApiService } from "src/app/core/services/browser-api/browser-api.service";
import { DomainNameService } from "src/app/core/services/domain-name/domain-name.service";
import { OverlayService } from "src/app/core/services/overlay/overlay.service";
import { ClickActionChoices } from "src/app/home/models/home.model";

const NIKITA_RETAILER_APP_DOMAIN = "nikitaretailer.hyperinvento.com"

export interface ClickActionChoiceValue {
    id: string;
    url_path?: string;
}

export interface ClickActionForFlutter {
    value: string | { id: string };
    action: string;
    url_path: string;
}

export interface OverlayData {
    id?: string;
    headerText: string;
    bodyText: string;
    modalStyle?: string;
    closeIcon?: string;
    parentElementRef: ElementRef;
}

export class LinkHandler {
    domain = "";
    timer: ReturnType<typeof setTimeout> | null = null;
    private destroyed = false;
    private domObserver: MutationObserver | null = null;
    private lastProcessedAnchorCount = 0;
    private processedAnchors = new Set<HTMLAnchorElement>(); // Track processed anchors

    // ✅ UTM tracking - page name for utm_source parameter
    page: string | any = '';

    // Configurable constants
    private readonly CLICK_ACTION_DELAY = 500;
    private readonly DEFAULT_CLOSE_ICON = "/assets/images/cross-white.svg";

    // Injected services using modern Angular 20 inject() function
    protected renderer = inject(Renderer2);
    public elementRef = inject(ElementRef);
    protected router = inject(Router);
    protected overlayService = inject(OverlayService);
    protected browserApiService = inject(BrowserApiService);
    private domainNameService: DomainNameService = inject(DomainNameService);

    applicationUrl = this.domainNameService.getApplicationUrl();


    checkForClickActions(page?: string | any) {

        // ✅ Set page name for UTM tracking
        if (page) {
            this.page = page;
        }

        const anchorTags: HTMLCollectionOf<HTMLAnchorElement> = this.elementRef?.nativeElement?.getElementsByTagName("a");



        // Clear existing timer to prevent multiple executions
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }

        this.timer = setTimeout(() => {
            let processedCount = 0;
            let modalCount = 0;
            let navigationCount = 0;

            for (let i = 0; i < anchorTags?.length; i++) {
                const anchorTag = anchorTags[i];
                if (!anchorTag) continue;

                const dataset = anchorTag.dataset;
                if (dataset && Object.keys(dataset)?.length) {
                    const clickActionChoice = dataset['click_action_choice'] as ClickActionChoices;
                    const clickActionValue = dataset['click_action_value'] as string;

                    if (clickActionChoice) {

                        this.addClickEventBasedOnClickAction(clickActionChoice, clickActionValue, anchorTag, anchorTag.target, dataset);

                        processedCount++;

                        if (clickActionChoice === ClickActionChoices.modal) {
                            modalCount++;
                        } else {
                            navigationCount++;
                        }
                    }
                }
            }


        }, this.CLICK_ACTION_DELAY);
    }

    addClickEventBasedOnClickAction(clickActionChoice: ClickActionChoices, clickActionValue: any, element: HTMLAnchorElement, target: string, dataset: DOMStringMap) {
        const addedClickActionValue: ClickActionChoiceValue = clickActionValue ? this.convertToSafeJSON(clickActionValue) : '';
        const sectionType = dataset['section_type'] ?? 'banner'; // ✅ Extract section_type for UTM parameters

        if (clickActionChoice === ClickActionChoices.modal) {
            // Handle modal separately to avoid double click event binding

            this.setupModalClickEvent(element, dataset);
        } else {
            // Handle navigation

            this.renderer.listen(element, 'click', () =>
                target === '_blank' ?
                    this.redirectToNewTab(clickActionChoice, addedClickActionValue, sectionType) : // ✅ Pass sectionType
                    this.redirectToSameTab(clickActionChoice, addedClickActionValue, sectionType)  // ✅ Pass sectionType
            );
        }

        this.removeCustomAddedAttributes(element);
    }

    private setupModalClickEvent(element: HTMLAnchorElement, dataset: DOMStringMap) {
        const id = element?.getAttribute('id');
        const modalDataString = dataset['modal_data'];

        if (!modalDataString) {
            return;
        }

        try {
            const parsedAvailableModalData = JSON.parse(modalDataString as string);

            if (parsedAvailableModalData && Object.keys(parsedAvailableModalData)?.length) {
                const {
                    headerText = "",
                    bodyText = "",
                    modalStyle = "",
                    closeIcon = this.DEFAULT_CLOSE_ICON
                } = parsedAvailableModalData;

                if (headerText && bodyText) {
                    const overlayData: OverlayData = {
                        ...(id && { id }),
                        headerText,
                        bodyText,
                        modalStyle,
                        closeIcon,
                        parentElementRef: this.elementRef
                    };

                    this.renderer.listen(element, 'click', () => this.openModal(overlayData));
                }
            }
        } catch (error) {
            // Handle modal data parsing error silently
        }

        this.removeAttribute(element, 'data-modal_data');
    }

    redirectToSameTab(clickActionChoice: ClickActionChoices, addedClickActionValue: ClickActionChoiceValue, sectionType: string = 'banner') {
        let clickActionForFlutter: ClickActionForFlutter | undefined;

        if (path_based_links.includes(clickActionChoice)) {
            if (clickActionChoice === ClickActionChoices.page) {
                const currentPageUrl = addedClickActionValue?.id;
                /* Managing page */
                if (currentPageUrl) {
                    this.router.navigate(
                        [NAVIGATION_PATH_BY_CHOICE[clickActionChoice], addedClickActionValue?.id],
                        { queryParams: this.getQueryParams(sectionType) } // ✅ Add UTM params
                    );
                }
            } else if (clickActionChoice === ClickActionChoices.internal_page) {
                this.router.navigate(
                    [addedClickActionValue?.id],
                    { queryParams: this.getQueryParams(sectionType) } // ✅ Add UTM params
                );
            } else {
                if (!this.isWebViewAvailable() || !this.applicationUrl.includes(NIKITA_RETAILER_APP_DOMAIN)) {
                    this.router.navigate(
                        [NAVIGATION_PATH_BY_CHOICE[clickActionChoice], addedClickActionValue?.url_path || ''],
                        { queryParams: this.getQueryParams(sectionType) } // ✅ Add UTM params
                    );
                }
                clickActionForFlutter = {
                    value: "",
                    action: CLICK_ACTION_FOR_FLUTTER[clickActionChoice],
                    url_path: addedClickActionValue?.url_path || ''
                };
            }
        } else if (search_url_links.includes(clickActionChoice)) {
            if (!this.isWebViewAvailable() || !this.applicationUrl.includes(NIKITA_RETAILER_APP_DOMAIN)) {
                this.router.navigate(['/search'], {
                    queryParams: {
                        [SEARCH_QUERY_PARAM_KEYS[clickActionChoice]]: addedClickActionValue?.id,
                        ...this.getQueryParams(sectionType) // ✅ Merge UTM params
                    }
                });
            }
            clickActionForFlutter = this.manageClickActionForFlutter(clickActionChoice, addedClickActionValue);
        } else if (clickActionChoice === ClickActionChoices.link_url) {
            clickActionForFlutter = {
                value: "",
                action: CLICK_ACTION_FOR_FLUTTER[clickActionChoice],
                url_path: addedClickActionValue?.id
            };
            if (this.isWebViewAvailable()) {
                this.postMessage(clickActionForFlutter);
            }

            if (!this.applicationUrl.includes(NIKITA_RETAILER_APP_DOMAIN)) {
                // ✅ Add UTM params to external URLs
                const urlWithUTM = this.getUpdatedURL(addedClickActionValue?.id, sectionType);
                this.browserApiService.open(urlWithUTM, '_self');
            }
        }

        // Only post message if we have valid data
        if (this.isWebViewAvailable() && clickActionForFlutter) {
            this.postMessage(clickActionForFlutter);
        }
    }

    convertToSafeJSON(jsonString: string) {
        if (!jsonString) return '';

        try {
            const sanitizedString = jsonString.replace(/'/g, '"');
            return JSON.parse(sanitizedString);
        } catch (error) {
            // Handle JSON parsing error silently
            return '';
        }
    }

    redirectToNewTab(clickActionChoice: ClickActionChoices, addedClickActionValue: ClickActionChoiceValue, sectionType: string = 'banner') {
        let new_url_to_redirect = this.browserApiService.getLocation()?.origin + '/' + NAVIGATION_PATH_BY_CHOICE[clickActionChoice];
        let clickActionForFlutter: ClickActionForFlutter | undefined;

        if (search_url_links.includes(clickActionChoice)) {
            new_url_to_redirect = new_url_to_redirect + '/?' +
                [SEARCH_QUERY_PARAM_KEYS[clickActionChoice]] + '=' + addedClickActionValue?.id +
                '&' + this.getQueryParamsString(sectionType); // ✅ Add UTM params
            clickActionForFlutter = this.manageClickActionForFlutter(clickActionChoice, addedClickActionValue);
        } else if (path_based_links.includes(clickActionChoice)) {
            if (clickActionChoice === ClickActionChoices.page) {
                const currentPageUrl = addedClickActionValue?.id;
                /* Managing active page */
                if (currentPageUrl) {
                    new_url_to_redirect = new_url_to_redirect + '/' + currentPageUrl +
                        '?' + this.getQueryParamsString(sectionType); // ✅ Add UTM params
                }
            } else if (clickActionChoice === ClickActionChoices.internal_page) {
                new_url_to_redirect = this.browserApiService.getLocation()?.origin + '/' + addedClickActionValue?.id +
                    '?' + this.getQueryParamsString(sectionType); // ✅ Add UTM params
            } else {
                new_url_to_redirect = new_url_to_redirect + '/' + (addedClickActionValue?.url_path || '') +
                    '?' + this.getQueryParamsString(sectionType); // ✅ Add UTM params
                clickActionForFlutter = {
                    value: "",
                    action: CLICK_ACTION_FOR_FLUTTER[clickActionChoice],
                    url_path: addedClickActionValue?.url_path || ''
                };
            }
        } else if (clickActionChoice === ClickActionChoices.link_url) {
            clickActionForFlutter = {
                value: "",
                action: CLICK_ACTION_FOR_FLUTTER[clickActionChoice],
                url_path: addedClickActionValue?.id || ''
            };

            if (this.isWebViewAvailable()) {
                this.postMessage(clickActionForFlutter);
            }

            if (!this.applicationUrl.includes(NIKITA_RETAILER_APP_DOMAIN)) {
                // ✅ Add UTM params to external URLs
                const urlWithUTM = this.getUpdatedURL(addedClickActionValue?.id || '', sectionType);
                this.browserApiService.open(urlWithUTM, '_blank');
                return;
            }

        }

        // Only post message if we have valid data
        if (this.isWebViewAvailable() && clickActionForFlutter) {
            this.postMessage(clickActionForFlutter);
        }

        if (!this.applicationUrl.includes(NIKITA_RETAILER_APP_DOMAIN)) {
            this.browserApiService.open(new_url_to_redirect);
        }

    }

    private removeCustomAddedAttributes(element: HTMLAnchorElement) {
        this.removeAttribute(element, 'data-click_action_choice');
        this.removeAttribute(element, 'data-click_action_value');
    }

    openModal(overlayData: OverlayData) {
        const overlayRef = this.overlayService.open(ConfigureOverlayComponent, overlayData, true, 'centered');
        const parentElementId = overlayData?.id;

        /* Removing added overlay style */
        const subscription = overlayRef.afterClosed$.subscribe(() => {
            if (!this.destroyed) {
                const modalStyle = document.getElementById(`style-modal-${parentElementId}`);
                if (modalStyle) {
                    modalStyle.remove();
                }
            }
            subscription.unsubscribe();
        });
    }

    private removeAttribute(element: HTMLElement, attributeName: string) {
        this.renderer.removeAttribute(element, attributeName);
    }

    manageClickActionForFlutter(clickActionChoice: ClickActionChoices, addedClickActionValue: ClickActionChoiceValue): ClickActionForFlutter {
        if (clickActionChoice === ClickActionChoices.product_flag) {
            return {
                value: "",
                action: CLICK_ACTION_FOR_FLUTTER[clickActionChoice],
                url_path: addedClickActionValue?.id
            };
        } else {
            return {
                value: {
                    id: addedClickActionValue?.id
                },
                action: CLICK_ACTION_FOR_FLUTTER[clickActionChoice],
                url_path: ""
            };
        }
    }

    private postMessage(clickAction: ClickActionForFlutter) {
        if (!clickAction) return;

        try {
            if ((window as any)?.flutter_inappwebview) {
                (window as any)?.flutter_inappwebview?.callHandler('FlutterHandler', JSON.stringify(clickAction));
            }
        } catch (error) {
            // Handle Flutter message posting error silently
        }
    }

    // Check if Flutter webview is available
    protected isWebViewAvailable(): boolean {
        return !!(window as any)?.flutter_inappwebview;
    }

    // ✅ UTM Parameter Methods - Get UTM parameters as object
    protected getQueryParams(section_type: string = 'banner'): { utm_source: string; utm_medium: string } {
        return {
            utm_source: this.page || 'unknown',
            utm_medium: section_type
        };
    }

    // ✅ UTM Parameter Methods - Get UTM parameters as URL string
    protected getQueryParamsString(section_type: string = 'banner'): string {
        return `utm_source=${encodeURIComponent(this.page || 'unknown')}&utm_medium=${encodeURIComponent(section_type)}`;
    }

    // ✅ UTM Parameter Methods - Add UTM params to external URLs
    private getUpdatedURL(url: string, sectionType: string): string {
        if (!url) return url;

        try {
            const urlObject = new URL(url);
            const hasUTMMedium = urlObject.searchParams.has("utm_medium");
            const hasUTMSource = urlObject.searchParams.has("utm_source");

            if (hasUTMMedium || hasUTMSource) {
                return url;
            } else {
                const separator = url.includes('?') ? '&' : '?';
                return url + separator + this.getQueryParamsString(sectionType);
            }
        } catch {
            // If URL parsing fails, treat as relative URL
            const separator = url.includes('?') ? '&' : '?';
            return url + separator + this.getQueryParamsString(sectionType);
        }
    }

    // Cleanup method to be called by components extending LinkHandler
    destroy(): void {
        this.destroyed = true;

        // Clear timer
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }


    }
}